import React, { useEffect, useState, useCallback, FormEvent } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { User, usersApi } from '../../../../api';
import CreateUserDialog, { CreateUserForm } from '../../../Users/CreateUserDialog';
import EditUserDialog, { EditUserForm } from '../../../Users/EditUserDialog';
import UserTable from '../../../Users/UserTable';

function parseErrorDetail(errorData: { detail?: unknown }, fallback: string): string {
  if (Array.isArray(errorData.detail)) {
    return (
      errorData.detail
        .map((e: { loc?: string[]; msg?: string }) =>
          e.loc ? `${e.loc.slice(1).join('.')}: ${e.msg}` : e.msg
        )
        .filter(Boolean)
        .join('; ') || fallback
    );
  }
  if (typeof errorData.detail === 'string') return errorData.detail;
  return fallback;
}

const EMPTY_CREATE_FORM: CreateUserForm = {
  email: '',
  username: '',
  full_name: '',
  password: '',
  role: 'user',
  language: 'en',
};

const EMPTY_EDIT_FORM: EditUserForm = {
  full_name: '',
  role: 'user',
  language: 'en',
};

interface OrganisationUsersTabProps {
  organisationId: string;
  organisationName: string;
  onUsersChanged?: () => void;
}

const OrganisationUsersTab: React.FC<OrganisationUsersTabProps> = ({
  organisationId,
  organisationName,
  onUsersChanged,
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState<CreateUserForm>(EMPTY_CREATE_FORM);
  const [createError, setCreateError] = useState<string | null>(null);

  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState<EditUserForm>(EMPTY_EDIT_FORM);
  const [editError, setEditError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await usersApi.list({ organisation_id: organisationId, limit: 1000 });
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to fetch organisation users:', err);
    } finally {
      setLoading(false);
    }
  }, [organisationId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    try {
      const payload: Parameters<typeof usersApi.create>[0] = {
        email: createForm.email,
        username: createForm.username,
        password: createForm.password,
        role: createForm.role,
        language: createForm.language,
        organisation_id: organisationId,
      };
      if (createForm.full_name) payload.full_name = createForm.full_name;

      await usersApi.create(payload);

      setShowCreate(false);
      setCreateForm(EMPTY_CREATE_FORM);
      setCreateError(null);
      fetchUsers();
      onUsersChanged?.();
    } catch (err: any) {
      setCreateError(parseErrorDetail(err.response?.data ?? {}, 'Failed to create user'));
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setEditForm({
      full_name: user.full_name || '',
      role: user.role || 'user',
      language: user.language || 'en',
    });
    setEditError(null);
  };

  const handleCloseEdit = () => {
    setEditingUser(null);
    setEditError(null);
  };

  const handleEditSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    setEditError(null);
    const uid = editingUser.user_id || editingUser.id || '';
    try {
      await usersApi.update(uid, {
        full_name: editForm.full_name,
        role: editForm.role,
        language: editForm.language,
      });
      handleCloseEdit();
      fetchUsers();
    } catch (err: any) {
      setEditError(parseErrorDetail(err.response?.data ?? {}, 'Failed to update user'));
    }
  };

  const handleDelete = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await usersApi.delete(userId);
      fetchUsers();
      onUsersChanged?.();
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert('Failed to delete user');
    }
  };

  if (loading && users.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Users ({users.length})</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowCreate(true)}>
          Add User
        </Button>
      </Box>

      <UserTable users={users} onEdit={handleEdit} onDelete={handleDelete} />

      <CreateUserDialog
        open={showCreate}
        formData={createForm}
        formError={createError}
        organisationName={organisationName}
        onClose={() => {
          setShowCreate(false);
          setCreateError(null);
        }}
        onFormChange={setCreateForm}
        onSubmit={handleCreate}
      />

      <EditUserDialog
        open={editingUser !== null}
        formData={editForm}
        formError={editError}
        onClose={handleCloseEdit}
        onFormChange={setEditForm}
        onSubmit={handleEditSubmit}
      />
    </Box>
  );
};

export default OrganisationUsersTab;
